import React, { FC, ReactNode, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import { Box, Button, Collapse, ListItem, ListItemProps } from '@mui/material';
import ChevronDownIcon from '../icons/ChevronDown';
import ChevronRightIcon from '../icons/ChevronRight';
import useAppTheme from '../hooks/useAppTheme';

interface NavItemProps extends ListItemProps {
  active?: boolean;
  children?: ReactNode;
  depth: number;
  icon?: ReactNode;
  info?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
}

const NavItem: FC<React.PropsWithChildren<NavItemProps>> = (props) => {
  const theme = useAppTheme();
  const {
    active,
    children,
    depth,
    icon,
    info,
    open: openProp,
    path,
    title,
    ...other
  } = props;
  const [open, setOpen] = useState<boolean>(openProp);

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 12;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  // Branch
  if (children) {
    return (
            <ListItem
                disableGutters
                sx={{
                  display: 'block',
                  py: 0
                }}
                {...other}
            >
                <Button
                    endIcon={!open ? <ChevronRightIcon fontSize="small"/> : <ChevronDownIcon fontSize="small"/>}
                    onClick={handleToggle}
                    startIcon={icon}
                    sx={{
                      color: theme.sideNav.text.secondary,
                      fontWeight: 'fontWeightMedium',
                      justifyContent: 'flex-start',
                      pl: `${paddingLeft}px`,
                      pr: '4px',
                      py: '12px',
                      textAlign: 'left',
                      textTransform: 'none',
                      width: '100%'
                    }}
                    variant="text"
                >
                    <Box sx={{ flexGrow: 1 }}>
                        {title}
                    </Box>
                    {info}
                </Button>
                <Collapse in={open}>
                    {children}
                </Collapse>
            </ListItem>
    );
  }

  // Leaf
  return (
        <ListItem
            disableGutters
            sx={{
              display: 'flex',
              py: 0
            }}
        >
            <Button
                component={path && RouterLink}
                startIcon={icon}
                sx={{
                  color: theme.sideNav.text.secondary,
                  fontWeight: 'fontWeightMedium',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  pl: `${paddingLeft}px`,
                  pr: '8px',
                  py: '12px',
                  textTransform: 'none',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: theme.sideNav.active,
                  },
                  ...(
                    active && {
                      color: theme.sideNav.selected,
                      fontWeight: 'fontWeightBold',
                      '& svg': {
                        color: theme.sideNav.selected
                      },
                      backgroundColor: theme.sideNav.active
                    }
                  ),
                }}
                variant="text"
                to={path}
            >
                <Box sx={{ flexGrow: 1 }}>
                    {title}
                </Box>
                {info}
            </Button>
        </ListItem>
  );
};

export default NavItem;
